/**
 * Session-replay input masking policy.
 *
 * OWA-675: this lives in its own dependency-free module (rather than inside
 * sessionReplay.ts) so that forms collecting sensitive data can unit-test the
 * REAL policy against their REAL rendered DOM. Importing sessionReplay.ts into
 * a component test drags in its whole upload/transport graph.
 *
 * @module
 */

/**
 * Autocomplete values that identify a PCI-relevant field: `cc-number` covers
 * routing/account/card numbers, `cc-csc` the CVV, `cc-exp-*` the expiration.
 */
const PCI_AUTOCOMPLETE = new Set([
	'cc-number',
	'cc-csc',
	'cc-exp',
	'cc-exp-month',
	'cc-exp-year',
]);

/**
 * Does this input's VALUE have to be masked in the replay recording?
 *
 * rrweb runs with `maskAllInputs: true`, which does NOT mean every value is
 * masked — it means every input is routed through THIS function, and the
 * decision is made here. Returning false records the value verbatim, which is
 * what we want for ordinary fields. See the comment on `maskAllInputs` in
 * sessionReplay.ts for why the flag has to be true (OWA-693). Masking affects
 * only what the recording captures — it never changes what the user sees.
 *
 * An input is masked when:
 *   1. It carries `data-private` (set by FormElement when hidden=true, or set
 *      explicitly by callers).
 *   2. It is nested inside any element with `data-private` — e.g. the
 *      encrypted-Select wrapper.
 *   3. Its autocomplete identifies a PCI-relevant field. This catches the
 *      BankAccountCard routing & account-number inputs, which must stay
 *      visually readable for the user but must not appear in the recording.
 *   4. It is a password input. rrweb masks those by default, but ONLY while
 *      it is deciding on its own — once `maskAllInputs: true` hands the
 *      decision to us, that default no longer applies and omitting this
 *      branch would start recording passwords in the clear.
 *
 * @param {Element} element - the input element rrweb is about to record
 * @returns {boolean} true when the value must be replaced with asterisks
 */
export function shouldMaskInput(element: Element): boolean {
	const autocomplete = (element.getAttribute('autocomplete') || '').toLowerCase();
	const type = (element.getAttribute('type') || '').toLowerCase();

	return type === 'password' ||
		PCI_AUTOCOMPLETE.has(autocomplete) ||
		element.hasAttribute('data-private') ||
		element.closest('[data-private]') !== null;
}
